/* eslint-disable no-console */
import { useCallback, useEffect, useState } from 'react';
// A wrapper for "JSON.parse()"" to support "undefined" value
var parseJSON = function (value, parse) {
    if (parse === void 0) { parse = true; }
    try {
        return value === 'undefined' ? undefined : parse ? JSON.parse(value !== null && value !== void 0 ? value : '') : value;
    }
    catch (_a) {
        return undefined;
    }
};
/**
 * A callback that uses localStorage to retrieve values.
 *
 * @param {string} key The key to use for the localStorage
 * @param {any} initialValue The initial value to use if the key is not found in localStorage
 * @param {Object} options - options object.
 * @param {string} options.isStringify - controls if the value is stringified in the localstorage and needs to be parsed when retrived
 * @param {string} options.storageType - pass in the window storage method you would like to use. i.e: { storageType: window.sessionStorage }
 */
export function readLocalStorage(key, initialValue, parse, options) {
    if (parse === void 0) { parse = true; }
    return function () {
        // Prevent build error "window is undefined" but keep keep working
        if (typeof window === 'undefined') {
            return initialValue;
        }
        // Get the value from localStorage or return the initialValue with a warning if not found
        try {
            var resolvedStorage = (options === null || options === void 0 ? void 0 : options.storageType) || window.localStorage;
            var item = resolvedStorage.getItem(key);
            return item ? parseJSON(item, parse) : initialValue;
        }
        catch (error) {
            console.warn("Error reading localStorage key \u201C".concat(key, "\u201D:"), error);
            return initialValue;
        }
    };
}
/**
 * A custom hook that uses localStorage to store and retrieve values.
 *
 * @param {string} key The key to use for the localStorage
 * @param {any} initialValue The initial value to use if the key is not found in localStorage
 * @param {any} isStringify controls if the value is stringified in the localstorage and needs to be parsed when retrived
 * @param {Object} options - options object.
 * @param {string} options.isStringify - controls if the value is stringified in the localstorage and needs to be parsed when retrived
 * @param {string} options.storageType - (defaults to localStorage) pass in the window storage method you would like to use. i.e: { storageType: window.sessionStorage }
 * @returns {[any, SetValue<any>]} A tuple of the value and a setter function
 */
var useLocalStorage = function (key, initialValue, isStringify, // TODO: move all optional params into options object https://springhealth.atlassian.net/browse/CGX-2682
options) {
    if (isStringify === void 0) { isStringify = true; }
    // Get the item from localStorage
    var readValue = useCallback(readLocalStorage(key, initialValue, isStringify, options), [
        initialValue,
        key,
        isStringify,
    ]);
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    var _a = useState(readValue), storedValue = _a[0], setStoredValue = _a[1];
    // Return a wrapped version of useState's setter function that will store the new value in localStorage.
    var setValue = useCallback(function (value) {
        // Prevent build error "window is undefined" but keeps working
        if (typeof window == 'undefined') {
            console.warn("Tried setting localStorage key \u201C".concat(key, "\u201D even though environment is not a client"));
        }
        try {
            // Allow value to be a function so we have the same API as useState
            var newValue = value instanceof Function ? value(storedValue) : value;
            // Save to resolved storage
            var resolvedStorage = (options === null || options === void 0 ? void 0 : options.storageType) || window.localStorage;
            resolvedStorage.setItem(key, isStringify ? JSON.stringify(newValue) : newValue);
            // Save state
            setStoredValue(newValue);
            // Dispatch a custom event so every useLocalStorage hook are notified
            window.dispatchEvent(new Event('local-storage'));
        }
        catch (error) {
            console.warn("Error setting localStorage key \u201C".concat(key, "\u201D:"), error);
        }
    }, [key, storedValue, isStringify]);
    // Set the value to the initial value the first time useLocalStorage is called
    useEffect(function () {
        setStoredValue(readValue());
    }, []);
    return [storedValue, setValue];
};
export { useLocalStorage };
